import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Chip, CircularProgress, LinearProgress, Typography } from '@mui/material';
import { PolarAngleAxis, PolarGrid, PolarRadiusAxis, Radar, RadarChart, ResponsiveContainer } from 'recharts';
import ArrowBack from '@mui/icons-material/ArrowBack';
import AutoAwesomeOutlined from '@mui/icons-material/AutoAwesomeOutlined';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import GroupOutlined from '@mui/icons-material/GroupOutlined';
import HighlightOffOutlined from '@mui/icons-material/HighlightOffOutlined';
import LightbulbOutlined from '@mui/icons-material/LightbulbOutlined';
import MenuBookOutlined from '@mui/icons-material/MenuBookOutlined';
import ShieldOutlined from '@mui/icons-material/ShieldOutlined';
import TrendingUpOutlined from '@mui/icons-material/TrendingUpOutlined';
import { Title, useNotify, useTranslate } from 'react-admin';
import { useNavigate, useParams } from 'react-router-dom';
import { DIMENSION_KEYS, isReportRunning, qualityService, type QualityCourseInfo, type QualityReport, type QualityReportSummary } from '../../../services/qualityService';
import { ProvenanceCard } from './ProvenanceCard';
import './course-quality.css';

const POLL_MS = 4000;

// recharts 3 types the polar axes as returning ReactNode, which @types/react 19
// does not accept as a JSX element; the runtime components are fine.
const AngleAxis = PolarAngleAxis as unknown as React.FC<Record<string, unknown>>;
const RadiusAxis = PolarRadiusAxis as unknown as React.FC<Record<string, unknown>>;

const scoreClass = (score: number): string => (score >= 75 ? 'is-good' : score >= 50 ? 'is-warn' : 'is-low');
const formatDate = (iso: string | null): string => {
    if (!iso) return '–';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

const apiError = (error: unknown, fallback: string): string => {
    const data = (error as { response?: { data?: { message?: string; error?: string } } })?.response?.data;
    return data?.message ?? (data?.error && !/^[a-z_]+$/.test(data.error) ? data.error : fallback);
};

/* ───────────────────────── report parts ───────────────────────── */

const QualityRadar: React.FC<{ dimensions: NonNullable<QualityReport['dimensions']> }> = ({ dimensions }) => {
    const translate = useTranslate();
    const data = DIMENSION_KEYS.filter((k) => typeof dimensions[k] === 'number').map((k) => ({ dimension: translate(`courseQuality.dimensions.${k}`), score: dimensions[k] as number }));
    return (
        <div className="oa-cq-radar" role="img" aria-label={translate('courseQuality.radarLabel', { count: data.length })} data-testid="quality-radar">
            <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={data}>
                    <PolarGrid />
                    <AngleAxis dataKey="dimension" tick={{ fontSize: 12 }} />
                    <RadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10 }} />
                    <Radar name="Score" dataKey="score" stroke="var(--oa-primary)" fill="var(--oa-primary)" fillOpacity={0.3} />
                </RadarChart>
            </ResponsiveContainer>
        </div>
    );
};

const ReportView: React.FC<{ report: QualityReport }> = ({ report }) => {
    const translate = useTranslate();
    const dims = report.dimensions ?? {};
    const details = report.details;
    const compliance = details?.compliance;
    const readability = details?.readability;
    const learnerData = details?.learner_data ?? null;
    const coveredCount = compliance?.checklist.filter((c) => c.covered).length ?? 0;
    const score = report.overallScore ?? 0;

    return (
        <>
            <section className="oa-cq-card">
                <div className="oa-cq-score-grid">
                    <div className={`oa-cq-score ${scoreClass(score)}`}>
                        <Typography variant="body2" color="text.secondary">{translate('courseQuality.overall')}</Typography>
                        <div className="oa-cq-score-value" data-testid="overall-score">{score}<small>/100</small></div>
                        <Typography variant="caption" color="text.secondary">{translate('courseQuality.checkedAt', { date: formatDate(report.completedAt) })}</Typography>
                        {report.model && <Typography variant="caption" color="text.secondary">{translate('courseQuality.model', { model: report.model })}</Typography>}
                    </div>
                    <div>
                        <QualityRadar dimensions={dims} />
                        <div className="oa-cq-dims">
                            {DIMENSION_KEYS.map((k) => (
                                <div className="oa-cq-dim" key={k}>
                                    <span>{translate(`courseQuality.dimensions.${k}`)}</span>
                                    <strong>{typeof dims[k] === 'number' ? `${dims[k]}` : translate('courseQuality.notApplicable')}</strong>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {compliance?.applicable && (
                <section className="oa-cq-card" data-testid="quality-compliance">
                    <h2 className="oa-cq-card-title">
                        <span className="oa-cq-icon"><ShieldOutlined fontSize="small" /></span>
                        {translate('courseQuality.compliance.title')}
                        <Chip size="small" sx={{ ml: 'auto' }} label={translate('courseQuality.compliance.covered', { covered: coveredCount, total: compliance.checklist.length })} />
                    </h2>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{translate('courseQuality.compliance.hint')}</Typography>
                    <div className="oa-cq-checklist">
                        {compliance.checklist.map((item) => (
                            <div key={item.item} className={`oa-cq-check ${item.covered ? 'is-covered' : 'is-missing'}`}>
                                <span className="oa-cq-check-icon">{item.covered ? <CheckCircleOutline fontSize="small" /> : <HighlightOffOutlined fontSize="small" />}</span>
                                <span>
                                    {translate(`courseQuality.compliance.items.${item.item}`, { _: item.label })}
                                    {item.evidence ? <span className="oa-cq-check-evidence">{translate('courseQuality.compliance.in', { evidence: item.evidence })}</span> : !item.covered ? <span className="oa-cq-check-evidence">{translate('courseQuality.compliance.missing')}</span> : null}
                                </span>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {readability && readability.score !== null && readability.lessons.length > 0 && (
                <section className="oa-cq-card" data-testid="quality-readability">
                    <h2 className="oa-cq-card-title">
                        <span className="oa-cq-icon"><MenuBookOutlined fontSize="small" /></span>
                        {translate('courseQuality.readability.title')}
                        <Chip size="small" sx={{ ml: 'auto' }} label={translate('courseQuality.readability.badge', { grade: readability.grade?.toFixed(1) ?? '–', score: readability.score })} />
                    </h2>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{translate('courseQuality.readability.hint')}</Typography>
                    <div className="oa-cq-readability">
                        {readability.lessons.map((lesson) => (
                            <div className="oa-cq-readability-row" key={lesson.lesson_id ?? lesson.title}>
                                <span>{lesson.title}</span>
                                <span>
                                    <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>{translate('courseQuality.readability.words', { words: lesson.words })}</Typography>
                                    <Chip size="small" className={`oa-cq-severity ${lesson.grade > 10 ? 'is-high' : lesson.grade > 8 ? 'is-medium' : 'is-low'}`} label={translate('courseQuality.readability.grade', { grade: lesson.grade.toFixed(1) })} />
                                </span>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {learnerData && (
                <section className="oa-cq-card" data-testid="quality-learner-data">
                    <h2 className="oa-cq-card-title">
                        <span className="oa-cq-icon"><GroupOutlined fontSize="small" /></span>
                        {translate('courseQuality.learnerData.title')}
                        {learnerData.sufficient && learnerData.completion_rate !== null && (
                            <Chip size="small" sx={{ ml: 'auto' }} label={`${translate('courseQuality.learnerData.completionRate')} ${learnerData.completion_rate} %`} />
                        )}
                    </h2>
                    {!learnerData.sufficient ? (
                        <Typography variant="body2" color="text.secondary">{translate('courseQuality.learnerData.insufficient', { count: learnerData.enrollments })}</Typography>
                    ) : (
                        <>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{translate('courseQuality.learnerData.hint')}</Typography>
                            <div className="oa-cq-learner-kpis">
                                <div><span>{translate('courseQuality.learnerData.enrollments')}</span><strong>{learnerData.enrollments}</strong></div>
                                <div><span>{translate('courseQuality.learnerData.completionRate')}</span><strong>{learnerData.completion_rate ?? '–'} %</strong></div>
                                <div><span>{translate('courseQuality.learnerData.inProgress')}</span><strong>{learnerData.in_progress}</strong></div>
                                <div><span>{translate('courseQuality.learnerData.avgScore')}</span><strong>{learnerData.avg_score ?? '–'}</strong></div>
                                <div><span>{translate('courseQuality.learnerData.avgTime')}</span><strong>{learnerData.avg_time_minutes !== null ? translate('courseQuality.learnerData.minutes', { minutes: learnerData.avg_time_minutes }) : '–'}</strong></div>
                            </div>
                            {learnerData.lessons.length > 0 && (
                                <div className="oa-cq-readability">
                                    <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5 }}>{translate('courseQuality.learnerData.funnel')}</Typography>
                                    {learnerData.lessons.map((lesson, index) => (
                                        <div className={`oa-cq-readability-row${lesson.drop_off ? ' is-drop-off' : ''}`} key={lesson.lesson_id ?? `${index}-${lesson.title}`}>
                                            <span>{index + 1}. {lesson.title}</span>
                                            <span className="oa-cq-funnel-meta">
                                                <Typography variant="caption" color="text.secondary">
                                                    {lesson.users_started} {translate('courseQuality.learnerData.started')} → {lesson.users_completed} {translate('courseQuality.learnerData.completed')}
                                                    {lesson.completion_rate !== null ? ` (${lesson.completion_rate} %)` : ''}
                                                    {lesson.exit_rate !== null && lesson.exit_rate > 0 ? ` · ${translate('courseQuality.learnerData.exit', { rate: lesson.exit_rate })}` : ''}
                                                    {lesson.avg_score !== null ? ` · ${translate('courseQuality.learnerData.score', { score: lesson.avg_score })}` : ''}
                                                </Typography>
                                                {lesson.drop_off && <Chip size="small" className="oa-cq-severity is-high" label={translate('courseQuality.learnerData.dropOff')} />}
                                                {lesson.low_score && <Chip size="small" className="oa-cq-severity is-medium" label={translate('courseQuality.learnerData.lowScore')} />}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </section>
            )}

            {report.suggestions && report.suggestions.length > 0 && (
                <section className="oa-cq-card" data-testid="quality-suggestions">
                    <h2 className="oa-cq-card-title"><span className="oa-cq-icon"><LightbulbOutlined fontSize="small" /></span>{translate('courseQuality.suggestions')}</h2>
                    <div className="oa-cq-list">
                        {report.suggestions.map((s, i) => (
                            <div className="oa-cq-item" key={i}>
                                <div className="oa-cq-item-head">
                                    <Chip size="small" className={`oa-cq-severity is-${s.severity}`} label={translate(`courseQuality.severity.${s.severity}`)} />
                                    <span>{translate(`courseQuality.dimensions.${s.category}`, { _: s.category })}</span>
                                </div>
                                <div>{s.suggestion}</div>
                                {s.affected_lessons.length > 0 && <div className="oa-cq-item-affected">{translate('courseQuality.affected', { lessons: s.affected_lessons.join(', ') })}</div>}
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {report.strengths && report.strengths.length > 0 && (
                <section className="oa-cq-card" data-testid="quality-strengths">
                    <h2 className="oa-cq-card-title"><span className="oa-cq-icon"><TrendingUpOutlined fontSize="small" /></span>{translate('courseQuality.strengths')}</h2>
                    <div className="oa-cq-list">
                        {report.strengths.map((s, i) => (
                            <div className="oa-cq-item" key={i}>
                                <div className="oa-cq-item-head">{translate(`courseQuality.dimensions.${s.category}`, { _: s.category })}</div>
                                <div>{s.description}</div>
                            </div>
                        ))}
                    </div>
                </section>
            )}
        </>
    );
};

/* ───────────────────────── page ───────────────────────── */

/**
 * KI-Qualitätsprüfung v2 (Tranche B3, #489): latest report of a course with
 * radar, compliance checklist, readability, suggestions and strengths; new
 * analyses run in the worker and are polled.
 */
export const CourseQualityPage: React.FC = () => {
    const { courseId } = useParams<{ courseId: string }>();
    const id = Number(courseId);
    const translate = useTranslate();
    const notify = useNotify();
    const navigate = useNavigate();
    const [course, setCourse] = useState<QualityCourseInfo | null>(null);
    const [reports, setReports] = useState<QualityReportSummary[]>([]);
    const [latest, setLatest] = useState<QualityReport | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [starting, setStarting] = useState(false);

    const load = useCallback(async () => {
        try {
            const { course: c, reports: list } = await qualityService.listReports(id);
            setCourse(c);
            setReports(list);
            setLatest(list.length > 0 ? await qualityService.getReport(list[0].id) : null);
            setError(false);
        } catch {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { void load(); }, [load]);

    const running = useMemo(() => latest !== null && isReportRunning(latest.status), [latest]);
    useEffect(() => {
        if (!running) return undefined;
        const timer = window.setInterval(() => { void load(); }, POLL_MS);
        return () => window.clearInterval(timer);
    }, [running, load]);

    const start = async () => {
        setStarting(true);
        try {
            await qualityService.start(id);
            notify(translate('courseQuality.started'), { type: 'info' });
            await load();
        } catch (e) {
            notify(apiError(e, translate('courseQuality.startError')), { type: 'error' });
        } finally {
            setStarting(false);
        }
    };

    const backTarget = course?.contentFormat === 'blocks' ? `/courses/${id}/builder` : `/courses/${id}/lessons`;

    return (
        <div className="oa-course-quality">
            <Title title={translate('courseQuality.title')} />
            <div className="oa-cq-header">
                <Button startIcon={<ArrowBack />} onClick={() => navigate(backTarget)}>{translate('courseQuality.back')}</Button>
                <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>{translate('courseQuality.title')}</Typography>
                <Chip size="small" label={translate('courseQuality.badge')} />
                {course && <Typography variant="body1" color="text.secondary">{course.title}</Typography>}
                <Button
                    variant="contained"
                    sx={{ ml: 'auto' }}
                    onClick={() => void start()}
                    disabled={starting || running || loading || error}
                    startIcon={starting ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeOutlined />}
                    data-testid="start-quality-check"
                >
                    {translate('courseQuality.start')}
                </Button>
            </div>
            <Alert severity="info" sx={{ mb: 2 }} data-testid="quality-ai-disclaimer">{translate('courseQuality.disclaimer')}</Alert>

            <ProvenanceCard courseId={id} />

            {loading && <LinearProgress sx={{ mb: 2 }} />}
            {error && !loading && (
                <div className="oa-cq-empty">
                    <Typography>{translate('courseQuality.loadError')}</Typography>
                    <Button sx={{ mt: 1 }} onClick={() => { setLoading(true); void load(); }}>{translate('ra.action.refresh')}</Button>
                </div>
            )}
            {!loading && !error && latest === null && (
                <div className="oa-cq-empty" data-testid="quality-empty">
                    <Typography variant="h6">{translate('courseQuality.empty.title')}</Typography>
                    <Typography variant="body2">{translate('courseQuality.empty.hint')}</Typography>
                </div>
            )}
            {latest && running && (
                <div className="oa-cq-running" data-testid="quality-running"><CircularProgress size={20} /><span>{translate('courseQuality.running')}</span></div>
            )}
            {latest && latest.status === 'failed' && (
                <Alert severity="error" sx={{ mb: 2 }}>{translate('courseQuality.failed', { error: latest.errorMessage ?? '–' })}</Alert>
            )}
            {latest && latest.status === 'completed' && <ReportView report={latest} />}

            {reports.length > 1 && (
                <section className="oa-cq-card" data-testid="quality-history">
                    <h2 className="oa-cq-card-title">{translate('courseQuality.history')}</h2>
                    <div className="oa-cq-history">
                        {reports.slice(1).map((r) => (
                            <div className="oa-cq-history-row" key={r.id}>
                                <span>{formatDate(r.createdAt)}</span>
                                <span>{translate('courseQuality.historyRow', { score: r.overallScore ?? '–', status: translate(`courseQuality.status.${r.status}`) })}</span>
                            </div>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
};

export default CourseQualityPage;
